import { useApiTradeV1MaterialListingsForBuyerCreate } from 'api';
import {
  AllowedMaterialSpecAutocomplete,
  Form,
  IncotermSelectField,
  type InferType,
  MaterialGradeAutocomplete,
  PercentageField,
  PortAutocomplete,
  TextField,
  TradeCompanyAutocomplete,
  TradeCompanySiteSelector,
  useForm,
  yupResolver,
} from 'form';
import { useEffect, useState } from 'react';
import { Box, Button, Page, SectionContainer } from 'ui';

import type { PricedSupplySignal } from '~/price-calculator/types';

import { PricedSupplySignalsGrid } from '../components';
import { buyPriceCalculatorSchema } from './schema';

type FormValues = InferType<typeof buyPriceCalculatorSchema>;

export const BuyPriceCalculator = () => {
  const [pricedSupplySignals, setPricedSupplySignals] = useState<PricedSupplySignal[]>([]);
  const form = useForm({
    defaultValues: {
      target_margin: '6',
      tonnes: '20',
    },
    resolver: yupResolver(buyPriceCalculatorSchema),
  });
  const { watch } = form;
  const buyer = watch('buyer_id');

  const {
    mutate: createMaterialListings,
    isPending,
    data: listingsResponse,
  } = useApiTradeV1MaterialListingsForBuyerCreate();

  const handleSubmit = (values: FormValues) => {
    createMaterialListings({
      data: {
        ...values,
        destination_site_id: values.destination_site_id ?? null,
        material_grade_id: values.material_grade_id ?? null,
        port_of_discharge_id: values.port_of_discharge_id ?? null,
      },
    });
  };

  useEffect(() => {
    if (listingsResponse) {
      const _pricedSupplySignals: PricedSupplySignal[] = [];
      for (const listing of listingsResponse.listings) {
        _pricedSupplySignals.push({
          buyer_price_per_tonne: '',
          created_at: listing.created_at,
          hubspot_url: listing.signal_hubspot_link,
          id: listing.id,
          material_grade: listing.material_grade,
          material_spec: listing.material_spec,
          port_of_discharge: listing.port_of_discharge,
          port_of_loading: listing.port_of_loading,
          price_calculator_error: listing.price_calculator_error,
          price_details: listing.price_details,
          seller: listing.seller,
          seller_currency: listing.seller_price_per_tonne_currency,
          seller_incoterm: listing.seller_incoterm,
          seller_price_per_tonne: listing.seller_price_per_tonne ? listing.seller_price_per_tonne : '',
          tonnes: listing.available_tonnes,
          tonnes_per_load: listing.tonnes_per_load,
          updated_at: listing.updated_at,
        });
      }
      setPricedSupplySignals(_pricedSupplySignals);
    }
  }, [listingsResponse]);

  return (
    <Page>
      <Page.Title title="Buy price calculator" />

      <Form {...form} onSubmit={handleSubmit}>
        <SectionContainer title="Buyer details" columns={2}>
          <TradeCompanyAutocomplete name="buyer_id" label="Buyer" onlyBuyers={true} required />
          <TradeCompanySiteSelector name="destination_site_id" label="Site" tradeCompanyId={buyer} />
          <IncotermSelectField name="buyer_incoterm" label="Buyer Incoterm" required />
          <PortAutocomplete name="port_of_discharge_id" label="Port" />
        </SectionContainer>
        <SectionContainer title="Material" columns={2}>
          <AllowedMaterialSpecAutocomplete name="material_spec_id" label="Material" />
          <MaterialGradeAutocomplete name="material_grade_id" label="Grade" />
          <TextField
            type="number"
            name="tonnes"
            label="Tonnes required"
            helperText="Defaults to tonnes per load if empty"
          />
        </SectionContainer>
        <SectionContainer title="Margin" columns={2}>
          <PercentageField name="target_margin" label="Target NTR %" />
        </SectionContainer>
        <Box display="flex" justifyContent="center">
          <Button type="submit" loading={isPending}>
            Get Prices
          </Button>
        </Box>
      </Form>
      <Box mt={2}>{!isPending && <PricedSupplySignalsGrid pricedSupplySignals={pricedSupplySignals} />}</Box>
    </Page>
  );
};
